/**
 * AssessmentPage — respondent experience
 *
 * Route: /r/:token   (token is either a UUID from pre-registration OR a legacy integer run_id)
 *
 * Flow:
 *   UUID (pre-registered):
 *     GET  /r/{token}        → run metadata + respondent context
 *     POST /r/{token}/submit → score + stamp submitted_at (idempotent guard on server)
 *   Integer (legacy shared URL):
 *     GET  /runs/{runId}        → run metadata
 *     POST /runs/{runId}/submit → score + store (anonymous UUID generated at submit time)
 */

import { useState, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import type { ResponseMap } from '../types'
import { CODEC_ITEMS, CODEC_DIMS } from '../data/codec-items'
import { HFA_ITEMS, HFA_DIMS } from '../data/hfa-items'
import { countAnswered as countCodec, CODEC_TOTAL_ITEMS } from '../scoring/codec'
import { countAnswered as countHfa, HFA_TOTAL_ITEMS } from '../scoring/hfa'
import { DimensionBlock } from '../components/assessment/DimensionBlock'
import styles from './AssessmentPage.module.css'

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_URL
  || 'https://clutch-ocm-scorer-uqrk7imnlq-uc.a.run.app'

/** UUID v4 pattern — distinguishes pre-registered token from legacy integer run_id */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUUID(s: string): boolean {
  return UUID_RE.test(s)
}

interface RunMeta {
  run_id: number
  instrument_code: string   // 'CODEC-v3' | 'HFA-v3'
  instrument_label: string
  engagement_name: string
  context_description: string
  run_label: string
  client_name: string
  // Present in token flow only
  respondent_id?: string
  department?: string | null
  team?: string | null
  tenure_band?: string | null
  already_submitted?: boolean
}

/** Fetch run metadata — routes to token endpoint or legacy endpoint based on format */
async function fetchMeta(token: string): Promise<RunMeta> {
  const url = isUUID(token)
    ? `${API_BASE}/r/${token}`
    : `${API_BASE}/runs/${token}`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Assessment not found (${res.status})`)
  return res.json()
}

/** Submit answers — uses /r/{token}/submit for UUID, /runs/{id}/submit for legacy */
async function submitAnswers(token: string, responses: ResponseMap) {
  const url = isUUID(token)
    ? `${API_BASE}/r/${token}/submit`
    : `${API_BASE}/runs/${token}/submit`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ responses }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `Submission failed (${res.status})`)
  }
  return res.json()
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function LoadingScreen() {
  return (
    <div className={styles.stateScreen}>
      <div className={styles.spinner} />
      <p>Loading your assessment…</p>
    </div>
  )
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className={styles.stateScreen}>
      <div className={styles.errorIcon}>!</div>
      <h2>Something went wrong</h2>
      <p>{message}</p>
      <p className={styles.errorSub}>
        If this link was sent to you by a Clutch consultant, please contact them for a new link.
      </p>
    </div>
  )
}

function ThankYouScreen(_: { meta: RunMeta }) {
  return (
    <div className={styles.stateScreen}>
      <div className={styles.thankYouIcon}>✓</div>
      <div className={styles.eyebrow}>Assessment Complete</div>
      <h2 className={styles.thankYouTitle}>Thank you for your response</h2>
      <p className={styles.thankYouSub}>
        Your answers have been recorded. Your Clutch consultant will use this
        input to tailor the support plan for your team.
      </p>
      <p className={styles.thankYouClose}>
        You may close this window.
      </p>
    </div>
  )
}

function AlreadySubmittedScreen() {
  return (
    <div className={styles.stateScreen}>
      <div className={styles.thankYouIcon}>✓</div>
      <div className={styles.eyebrow}>Already Submitted</div>
      <h2 className={styles.thankYouTitle}>Your response has been recorded</h2>
      <p className={styles.thankYouSub}>
        It looks like you've already completed this assessment. Each link can
        only be submitted once to keep results accurate.
      </p>
      <p className={styles.thankYouClose}>
        If you believe this is an error, please contact your Clutch consultant.
      </p>
    </div>
  )
}

function IntroScreen({ meta, onStart }: { meta: RunMeta; onStart: () => void }) {
  const isCodec = meta.instrument_code === 'CODEC-v3'
  return (
    <div className={styles.introWrap}>
      <div className={styles.introCard}>
        <div className={styles.eyebrow}>
          {isCodec ? 'CODE-C Readiness–Resistance Assessment' : 'HFA Habit Adoption Assessment'}
        </div>
        <h1 className={styles.introTitle}>{meta.engagement_name}</h1>
        <p className={styles.introClient}>{meta.run_label} Assessment</p>

        {meta.context_description && (
          <div className={styles.ctxCard}>
            <span className={styles.ctxLabel}>
              {isCodec ? 'Change context' : 'Target habit'}
            </span>
            <p>{meta.context_description}</p>
          </div>
        )}

        <div className={styles.introMeta}>
          <div className={styles.introMetaItem}>
            <span className={styles.introMetaVal}>{isCodec ? '25' : '17'}</span>
            <span className={styles.introMetaLbl}>Questions</span>
          </div>
          <div className={styles.introMetaItem}>
            <span className={styles.introMetaVal}>{isCodec ? '8–10' : '5–7'}</span>
            <span className={styles.introMetaLbl}>Minutes</span>
          </div>
          <div className={styles.introMetaItem}>
            <span className={styles.introMetaVal}>1–5</span>
            <span className={styles.introMetaLbl}>Scale</span>
          </div>
        </div>

        <p className={styles.introInstr}>
          For each statement, select the number that best reflects your honest experience right now.
          There are no right or wrong answers — your candid input is what helps most.
        </p>

        <button className={styles.startBtn} onClick={onStart}>
          Begin Assessment →
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

function ProgressBar({ answered, total }: { answered: number; total: number }) {
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0
  return (
    <div className={styles.progressBar}>
      <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      <span className={styles.progressLabel}>{answered} / {total} answered</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type PageState = 'loading' | 'error' | 'already_submitted' | 'intro' | 'questions' | 'submitting' | 'done'

const CODEC_DIM_ITEMS = (dim: string) => CODEC_ITEMS.filter(i => i.dim === dim)
const HFA_DIM_ITEMS   = (dim: string) => HFA_ITEMS.filter(i => i.dim === dim)

export function AssessmentPage() {
  // Route param is "token" — may be a UUID (pre-registered) or integer (legacy)
  const { token } = useParams<{ token: string }>()

  const [pageState, setPageState] = useState<PageState>('loading')
  const [meta,      setMeta]      = useState<RunMeta | null>(null)
  const [errorMsg,  setErrorMsg]  = useState('')
  const [responses, setResponses] = useState<ResponseMap>({})
  const [submitErr, setSubmitErr] = useState('')

  // Fetch run / token metadata on mount
  useEffect(() => {
    if (!token) { setErrorMsg('No assessment token in URL.'); setPageState('error'); return }
    fetchMeta(token)
      .then(m => {
        setMeta(m)
        // Pre-registered respondent who already submitted — show blocked state
        if (m.already_submitted) {
          setPageState('already_submitted')
        } else {
          setPageState('intro')
        }
      })
      .catch(e => { setErrorMsg(e.message); setPageState('error') })
  }, [token])

  const handleResponse = useCallback((itemId: string, value: number) => {
    setResponses(prev => ({ ...prev, [itemId]: value }))
  }, [])

  const handleSubmit = async () => {
    if (!meta || !token) return
    setSubmitErr('')
    setPageState('submitting')
    try {
      await submitAnswers(token, responses)
      setPageState('done')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Submission failed.'
      // 409 means the server already stamped submitted_at (race condition / double-tap)
      if (msg.toLowerCase().includes('already submitted')) {
        setPageState('already_submitted')
      } else {
        setSubmitErr(msg)
        setPageState('questions')
      }
    }
  }

  const isCodec  = meta?.instrument_code === 'CODEC-v3'
  const answered  = isCodec ? countCodec(responses) : countHfa(responses)
  const total     = isCodec ? CODEC_TOTAL_ITEMS : HFA_TOTAL_ITEMS
  const allDone   = answered === total

  // ── Render states ──────────────────────────────────────────────────────────

  if (pageState === 'loading')          return <LoadingScreen />
  if (pageState === 'error')            return <ErrorScreen message={errorMsg} />
  if (pageState === 'already_submitted') return <AlreadySubmittedScreen />
  if (pageState === 'done' && meta)     return <ThankYouScreen meta={meta} />
  if (pageState === 'intro' && meta)
    return <IntroScreen meta={meta} onStart={() => setPageState('questions')} />

  if (!meta) return null

  // ── Questions ──────────────────────────────────────────────────────────────

  const dims  = isCodec ? CODEC_DIMS : HFA_DIMS
  const dimItems = (dimKey: string) => {
    if (isCodec) {
      return CODEC_DIM_ITEMS(dimKey).map(i => ({
        id: i.id,
        text: i.text,
        reverseCoded: i.reverseCoded,
      }))
    } else {
      return HFA_DIM_ITEMS(dimKey).map(i => ({
        id: i.id,
        text: i.text,
        reverseCoded: false,
        noteText: i.noteText ?? undefined,
      }))
    }
  }

  return (
    <div className={styles.page}>
      {/* Context banner */}
      <div className={styles.contextBanner}>
        <span className={styles.ctxBannerLabel}>
          {isCodec ? 'Change context' : 'Target habit'}
        </span>
        <span className={styles.ctxBannerText}>{meta.context_description}</span>
      </div>

      <div className="wrap">
        {/* Dimensions */}
        <div className={styles.dimensions}>
          {dims.map(dim => (
            <DimensionBlock
              key={dim.key}
              dimKey={dim.key}
              label={dim.label}
              subtitle={dim.subtitle}
              icon={dim.icon}
              items={dimItems(dim.key)}
              responses={responses}
              onResponse={handleResponse}
              variant={isCodec ? 'aqua' : 'magenta'}
            />
          ))}
        </div>
      </div>

      {/* Sticky action bar */}
      <div className={styles.actionBar}>
        <ProgressBar answered={answered} total={total} />

        {submitErr && (
          <p className={styles.submitErr}>{submitErr}</p>
        )}

        <button
          className={styles.submitBtn}
          disabled={!allDone || pageState === 'submitting'}
          onClick={handleSubmit}
        >
          {pageState === 'submitting' ? 'Submitting…' : allDone
            ? 'Submit Assessment →'
            : `Answer all ${total - answered} remaining questions to submit`}
        </button>
      </div>
    </div>
  )
}
